/**
 * useTermsQueries — 약관 최종 버전 react-query 훅.
 *
 * BE 가 (division, 언어) 별로 1건의 latest terms 를 반환하며, Redis `shared:terms:latest:{div}:{lang}`
 * 캐시 사용. 요청 언어의 번역본이 없으면 정본(ko) 본문이 내려오고 응답의 languageCode 로 구분된다.
 * staleTime 은 짧게 유지 (어드민 mutation 즉시 evict → 다음 fetch 가 반영).
 *
 * 404 (미등록) → null 로 fallback (UI 가 빈 영역 또는 안내 문구로 처리).
 */
import { useQuery } from "@tanstack/react-query";
import { fetchLatestTerms, type LatestTerms } from "../api/terms";
import { ApiError } from "../lib/errors";

export function latestTermsKey(divisionCode: string, language: string) {
  // 언어가 키에 들어가야 언어를 바꿨을 때 이전 언어의 본문이 캐시에서 그대로 나오지 않는다.
  return ["terms", divisionCode, "latest", language] as const;
}

/**
 * 약관 최신 버전 조회 hook.
 *
 * @param divisionCode TERMS_DIV 공통코드. 빈 문자열이면 fetch skip (OSS 처럼 BE 약관 없는 케이스).
 * @param language     앱 표시 언어(ko|en|ja).
 */
export function useLatestTermsQuery(divisionCode: string, language: string) {
  const enabled = divisionCode.length > 0;
  return useQuery<LatestTerms | null>({
    queryKey: latestTermsKey(divisionCode, language),
    queryFn: async () => {
      try {
        return await fetchLatestTerms(divisionCode, language);
      } catch (err) {
        // 404 = 운영자가 아직 등록 안 한 division. UI 는 안내 문구로 fallback.
        if (err instanceof ApiError && err.httpStatus === 404) return null;
        throw err;
      }
    },
    enabled,
    staleTime: 60_000,
    retry: (failureCount, err) => {
      // 404 는 retry 무의미 (이미 catch 됐지만 방어).
      if (err instanceof ApiError && err.httpStatus === 404) return false;
      return failureCount < 1;
    },
  });
}
